import { useState, useMemo, useEffect } from "react";
import { Button, Grid, Box, Divider } from "@mzc-pdc/ui";
import { useTranslation } from "react-i18next";
import AdaptivePopper from "@components_v2/adaptive-popper";
import BaseFilterChip from "@components_v2/search-filters/base-filter-chip";
import theme from "@styles/theme";
import { convertToUTCDateTime } from "@utils/convertToUTCDateTime";

const FilterLayerPopper = ({
    open,
    anchorEl,
    filters,
    editTempValue,
    getValueByParam,
    getMaxCount,
    onHandleDeleteChip,
    onClickSubmit,
    onClose,
}) => {
    const { t } = useTranslation();
    const [selectedFilterId, setSelectedFilterId] = useState(null);

    const selectedFilter = useMemo(() => filters.find((f) => f.id === selectedFilterId), [filters, selectedFilterId]);

    // 초기 열림 시 첫 번째 필터 자동 선택 + resize로 선택 필터가 빠져나간 경우 fallback
    useEffect(() => {
        const currentIds = filters.map((f) => f.id);
        if (!selectedFilterId || !currentIds.includes(selectedFilterId)) {
            setSelectedFilterId(currentIds.length > 0 ? currentIds[0] : null);
        }
    }, [filters, selectedFilterId]);

    const handleApply = () => {
        const submitValues = {};
        filters.forEach(({ id, type }) => {
            if (type === "assetDateRange") {
                if (!editTempValue.startAt && !editTempValue.endAt) return;
                submitValues.startAt = convertToUTCDateTime(editTempValue.startAt, "start", true);
                submitValues.endAt = convertToUTCDateTime(editTempValue.endAt, "end", true);
                return;
            }
            if (!editTempValue[id]) return;

            if (type === "users") {
                submitValues[id] = editTempValue[id];
                submitValues[`${id}Name`] = editTempValue[`${id}Name`];
            } else if (type === "categories") {
                submitValues[id] = editTempValue[id];
                submitValues.categoryGroup = editTempValue.categoryGroup;
            } else {
                submitValues[id] = editTempValue[id];
            }
        });

        onClickSubmit(submitValues);
        onClose();
    };

    return (
        <AdaptivePopper
            open={open}
            anchorEl={anchorEl}
            placement="bottom-end"
            disablePortal={true}
            offset={[0, 12]}
            minHeight={400}
            maxHeight={600}
            onClickAway={onClose}
            wrapperSx={{ width: 800, px: 0, py: 2 }}
        >
            {/* 2컬럼 본문 — content 자체는 스크롤하지 않고, 좌우 패널이 각각 스크롤 */}
            <Grid container data-role="content" sx={{ height: "100%", overflowY: "hidden", flexWrap: "nowrap", mb: 2 }}>
                {/* 좌측 패널: 필터 칩 목록 (세로) — 독립 스크롤 */}
                <Grid
                    item
                    sx={{
                        flex: "0 0 320px",
                        borderRight: `1px solid ${theme.palette.greySecondary[100]}`,
                        overflowY: "auto",
                        height: "100%",
                        px: theme.spacing(2),
                        py: theme.spacing(1.5),
                    }}
                >
                    <Grid container flexDirection="column" gap={theme.spacing(0.75)}>
                        {filters.map((item) => (
                            <Grid container key={item.id} alignItems="center" gap={theme.spacing(0.75)}>
                                {/* 활성 dot: committed 값 없이 임시값이 있거나, committed 값과 임시값이 다를 때 표시 */}
                                <Box
                                    sx={{
                                        width: 6,
                                        height: 6,
                                        borderRadius: "50%",
                                        bgcolor:
                                            (!item.value && editTempValue[item.id]) ||
                                            (item.value && editTempValue[item.id] !== item.value)
                                                ? theme.palette.primary.main
                                                : "transparent",
                                    }}
                                />
                                <BaseFilterChip
                                    item={item}
                                    selected={item.id === selectedFilterId}
                                    onClick={() => setSelectedFilterId(item.id)}
                                    onDelete={item.value ? () => onHandleDeleteChip(item) : undefined}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Grid>

                {/* 우측 패널: 선택된 필터 편집 UI — 독립 스크롤 */}
                <Grid
                    item
                    sx={{
                        flex: 1,
                        px: theme.spacing(2),
                        pt: theme.spacing(1.5),
                        pb: 0,
                        overflowY: "hidden",
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {selectedFilter && (() => {
                        const maxCountContent = getMaxCount(selectedFilter.id, editTempValue[selectedFilter.id] ?? []);
                        return (
                            <>
                                <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", scrollbarGutter: "stable" }}>
                                    {getValueByParam(editTempValue, selectedFilter)}
                                </Box>

                                {maxCountContent && (
                                    <Box sx={{ flexShrink: 0 }}>
                                        <Divider sx={{ mt: theme.spacing(2) }} />
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "flex-end",
                                                pt: theme.spacing(2),
                                            }}
                                        >
                                            {maxCountContent}
                                        </Box>
                                    </Box>
                                )}
                            </>
                        );
                    })()}
                </Grid>
            </Grid>

            {/* 하단 Footer (고정) */}
            <Grid
                container
                data-role="footer"
                justifyContent="flex-end"
                gap={theme.spacing(0.75)}
                sx={{ px: theme.spacing(2), pt: theme.spacing(2), pb: 0 }}
            >
                <Button variant="text" size="small" color="greySecondary" onClick={onClose}>
                    {t("common::label::Cancel")}
                </Button>
                <Button variant="contained" size="small" color="primary" onClick={handleApply}>
                    {t("common::label::Apply")}
                </Button>
            </Grid>
        </AdaptivePopper>
    );
};

export default FilterLayerPopper;
